import React, { useState, useEffect } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Shield, Eye, EyeOff, AlertCircle, Loader2, Clock, KeyRound } from "lucide-react";

// Shared post-login destination — SINGLE SOURCE OF TRUTH consumed by both
// the post-login navigate() calls (non-MFA and MFA-verify success) and the
// already-authenticated /login route guard below. Keeping one function
// guarantees the guard cannot drift from the sign-in-success routing:
// internal, external, clinic (routed via /portal → App.js clinic bounce),
// and must_change_password all land byte-identically to today.
// Outside accounts route by ROLE as well as userType (2026-10): a law-firm
// role lands on the law-firm portal and a partner on the partner view,
// whatever user_type the row carries.
const LAW_FIRM_ROLES = new Set(["client", "firm_user", "firm_admin"]);
const isLawFirmRole = (role) => LAW_FIRM_ROLES.has(String(role || "").toLowerCase());
const isPartnerRole = (role) => String(role || "").toLowerCase() === "partner";

const destinationFor = (user) => {
  if (user.userType === 'external' || isLawFirmRole(user.role) || isPartnerRole(user.role)) return isPartnerRole(user.role) ? "/partner" : "/portal/dashboard";
  if (user.must_change_password) return "/change-password";
  if (user.role === "sales") return "/dashboard/sales";
  if (user.role === "collections") return "/dashboard/collections";
  if (user.role === "location") return "/crm-queue";
  return "/dashboard/sales";
};

export default function Login() {
  const { user, loading: authLoading, login, mfaVerify } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ identifier: "", password: "" });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionTimeout, setSessionTimeout] = useState(false);

  // MFA challenge state. Set when /auth/login returns
  // {mfa_required:true, mfa_token}. Cleared on success or when the
  // user clicks "back to sign in" (e.g. after a 5-min token expiry).
  const [mfaToken, setMfaToken] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [mfaSubmitting, setMfaSubmitting] = useState(false);

  // Check for session timeout on mount
  useEffect(() => {
    if (searchParams.get('timeout') === 'true' || sessionStorage.getItem('session_timeout') === 'true') {
      setSessionTimeout(true);
      sessionStorage.removeItem('session_timeout');
    }
  }, [searchParams]);

  // ── Already-authenticated route guard (2026-08) ─────────────────────────
  // Field diagnosis: Teams tab remounts navigate directly to
  // /login?source=teams. AuthContext boot restores the session correctly
  // (branch "restored", token present) — but the form previously rendered
  // regardless of auth state, so authenticated users saw a login screen.
  // Guard rules:
  //   • Wait for AuthContext to finish restoring — NEVER redirect on a
  //     not-yet-settled state and NEVER flash the form while it's loading.
  //   • MFA challenge (local mfaToken set) is not authenticated yet — the
  //     backend withholds the access token until /auth/mfa/verify, so `user`
  //     stays null during the challenge. `!mfaToken` is belt-and-suspenders.
  //   • Destination reuses destinationFor(user) — byte-identical to the
  //     path a fresh sign-in would follow (must_change_password included).
  //   • <Navigate replace> so no history entry is left behind.
  if (authLoading) return null;
  if (user && !mfaToken) {
    return <Navigate to={destinationFor(user)} replace />;
  }

  // Shared post-login navigation — used by both the non-MFA login
  // success and the MFA verify success. Keeping this in one place
  // ensures the two paths are byte-for-byte equivalent after login.
  const navigateAfterLogin = (u) => {
    navigate(destinationFor(u));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await login(form.identifier, form.password);
      // MFA branch: backend returned a challenge, no real token yet.
      if (result && result.mfaRequired) {
        setMfaToken(result.mfaToken);
        setMfaCode("");
        setUseRecoveryCode(false);
        return;
      }
      // Non-MFA branch — identical behaviour to before this ticket.
      navigateAfterLogin(result);
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed. Check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMfaSubmitting(true);
    try {
      const user = await mfaVerify(mfaToken, mfaCode.trim());
      navigateAfterLogin(user);
    } catch (err) {
      const detail = err.response?.data?.detail || "MFA verification failed.";
      // 5-min challenge expiry → bounce back to step 1 with a clear message.
      if (typeof detail === "string" && detail.toLowerCase().includes("expired")) {
        setMfaToken(null);
        setError("MFA challenge expired. Please sign in again.");
        return;
      }
      setError(detail);
    } finally {
      setMfaSubmitting(false);
    }
  };

  // Auto-detect if input looks like an email
  const isEmail = form.identifier.includes('@');

  return (
    <div className="flex h-screen w-full">
      {/* Left panel - branding */}
      <div
        className="hidden lg:flex lg:w-1/2 relative flex-col justify-between p-12"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1615438799799-4c5a3ca146bf?crop=entropy&cs=srgb&fm=jpg&q=85')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/80 to-slate-900/90" />
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
              <Shield size={26} className="text-white" />
            </div>
            <div>
              <p className="text-white font-bold text-xl" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                WestStar Portal
              </p>
              <p className="text-blue-200 text-sm">Unified Access Platform</p>
            </div>
          </div>
        </div>
        <div className="relative z-10 space-y-6">
          <div>
            <blockquote className="text-white/90 text-2xl font-light leading-relaxed" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              "One login for everyone."
            </blockquote>
            <p className="text-blue-300 text-sm mt-3">
              Staff members use their username. Law firm partners use their email.
            </p>
          </div>
          <div className="flex gap-4 text-xs text-blue-200">
            <span className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              HIPAA Compliant
            </span>
            <span className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              Role-based Access
            </span>
            <span className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              Full Audit Trail
            </span>
          </div>
        </div>
      </div>

      {/* Right panel - login form */}
      <div className="flex-1 flex flex-col items-center justify-center bg-slate-50 px-8 py-12">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-10 lg:hidden">
            <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
              <Shield size={20} className="text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              WestStar Portal
            </span>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
            {mfaToken ? (
              <>
                <h1 className="text-2xl font-bold text-slate-900 mb-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  Two-factor authentication
                </h1>
                <p className="text-sm text-slate-500 mb-6">
                  {useRecoveryCode
                    ? "Enter one of your 12-character recovery codes."
                    : "Enter the 6-digit code from your authenticator app."}
                </p>

                {error && (
                  <div
                    data-testid="mfa-error"
                    className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 mb-6"
                  >
                    <AlertCircle size={16} className="flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <form onSubmit={handleMfaSubmit} className="space-y-5" data-testid="mfa-form">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      {useRecoveryCode ? "Recovery code" : "Authentication code"}
                    </label>
                    <input
                      data-testid="mfa-code-input"
                      type="text"
                      autoComplete="one-time-code"
                      autoFocus
                      inputMode={useRecoveryCode ? "text" : "numeric"}
                      // Server still validates exact format — these are
                      // light UX hints, not the source of truth.
                      maxLength={useRecoveryCode ? 14 : 6}
                      value={mfaCode}
                      onChange={(e) => setMfaCode(useRecoveryCode ? e.target.value.toUpperCase() : e.target.value.replace(/\D/g, ""))}
                      className="w-full h-11 px-4 rounded-lg border border-slate-200 bg-white text-sm tracking-widest text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={useRecoveryCode ? "ABCD-EFGH-JKLM" : "123456"}
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    data-testid="mfa-submit"
                    disabled={mfaSubmitting || !mfaCode.trim()}
                    className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {mfaSubmitting ? (
                      <><Loader2 size={16} className="animate-spin" /> Verifying...</>
                    ) : (
                      "Verify"
                    )}
                  </button>
                </form>

                <div className="mt-6 flex items-center justify-between text-xs">
                  <button
                    type="button"
                    data-testid="mfa-toggle-recovery"
                    onClick={() => { setUseRecoveryCode((v) => !v); setMfaCode(""); setError(""); }}
                    className="text-blue-600 hover:text-blue-700 inline-flex items-center gap-1.5 font-medium"
                  >
                    <KeyRound size={13} />
                    {useRecoveryCode ? "Use authenticator code instead" : "Use a recovery code instead"}
                  </button>
                  <button
                    type="button"
                    data-testid="mfa-back"
                    onClick={() => { setMfaToken(null); setMfaCode(""); setUseRecoveryCode(false); setError(""); }}
                    className="text-slate-400 hover:text-slate-600"
                  >
                    Back to sign in
                  </button>
                </div>
              </>
            ) : (
              <>
                <h1 className="text-2xl font-bold text-slate-900 mb-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  Welcome back
                </h1>
                <p className="text-sm text-slate-500 mb-6">
                  Sign in to continue to your dashboard
                </p>

                {/* Session Timeout Notice */}
                {sessionTimeout && (
                  <div
                    data-testid="session-timeout-notice"
                    className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-700 text-sm rounded-lg px-4 py-3 mb-6"
                  >
                    <Clock size={16} className="flex-shrink-0" />
                    <span>Your session has expired due to inactivity. Please sign in again.</span>
                  </div>
                )}

                {error && (
                  <div
                    data-testid="login-error"
                    className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 mb-6"
                  >
                    <AlertCircle size={16} className="flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Username or Email
                </label>
                <input
                  data-testid="login-identifier"
                  type="text"
                  autoComplete="username"
                  value={form.identifier}
                  onChange={(e) => setForm((f) => ({ ...f, identifier: e.target.value }))}
                  className="w-full h-11 px-4 rounded-lg border border-slate-200 bg-white text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Enter username or email"
                  required
                />
                {form.identifier && (
                  <p className="text-xs text-slate-400 mt-1.5">
                    {isEmail ? (
                      <span className="text-emerald-600">→ Signing in as Client Portal user</span>
                    ) : (
                      <span className="text-blue-600">→ Signing in as Staff user</span>
                    )}
                  </p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Password</label>
                <div className="relative">
                  <input
                    data-testid="login-password"
                    type={showPw ? "text" : "password"}
                    autoComplete="current-password"
                    value={form.password}
                    onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                    className="w-full h-11 pl-4 pr-11 rounded-lg border border-slate-200 bg-white text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors p-1"
                  >
                    {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                data-testid="login-submit"
                disabled={loading}
                className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Signing in...
                  </>
                ) : (
                  "Sign In"
                )}
              </button>
            </form>
              </>
            )}
          </div>

          {/* Help text */}
          <div className="mt-6 text-center">
            <p className="text-xs text-slate-400">
              <span className="font-medium text-slate-500">Staff:</span> Use your username
              <span className="mx-2">•</span>
              <span className="font-medium text-slate-500">Partners:</span> Use your email
            </p>
            <p className="text-xs text-slate-400 mt-2">
              Forgot password? Contact your administrator.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
